import {Slide} from '@aboutyou/transitions';
import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';
import {ImageType} from '../../../../types/Image';
import {getRatio} from '../../../atoms/Image/helpers';
import {Ratio} from '../../../atoms/Image/types';
import {Video as BaseVideo, VideoSourceType} from '../../../atoms/Video/Video';
import {
  ContentBlockCTA,
  ContentBlockCTAProps,
} from '../ContentBlockCTA/ContentBlockCTA';
import {CustomControls} from './CustomControls';
import {useVideoControls} from './hooks/useVideoControls';

interface VideoType {
  sources: VideoSourceType[];
  description: string;
  fallbackImage: ImageType & {aspectRatio: Ratio};
  aspectRatio: Ratio;
}

export interface ContentBlockVideoAutoPlayProps {
  className?: string;
  cta?: ContentBlockCTAProps;
  disableMuteUnmuteControl?: boolean;
  disablePlayPauseControl?: boolean;
  intersectionThreshold?: number;
  testId?: string;
  video: VideoType;
  customControlsPosition?: 'bottom' | 'top' | 'left-top';
  customControlsAlignment?: 'horizontal' | 'vertical';
  showControls?: boolean;
}

export const ContentBlockVideoAutoPlay: FC<ContentBlockVideoAutoPlayProps> = ({
  className,
  cta,
  disableMuteUnmuteControl = false,
  disablePlayPauseControl = false,
  intersectionThreshold = 0.5,
  testId = 'videoTile',
  video,
  customControlsPosition,
  customControlsAlignment,
}) => {
  const {widthRatio, heightRatio} = getRatio(video.aspectRatio);

  const [showPlayerControls, setShowPlayerControls] = useState(true);

  const {
    handleMuteToggleClick,
    handlePlayToggleClick,
    inView,
    muted,
    playing,
    videoHasAudio,
    onCanPlay,
    videoRef,
  } = useVideoControls(intersectionThreshold);

  return (
    <Wrapper className={className}>
      {cta && cta.text.text !== '' && (
        <ContentBlockCTA
          testId={`${testId}CTA`}
          text={cta.text}
          link={cta.link}
        />
      )}
      <Container data-testid={`${testId}Container`}>
        <Video
          aspectRatio={
            `${widthRatio} / ${heightRatio}` as `${number} / ${number}`
          }
          autoPlay
          data-testid={testId}
          loop
          muted={muted}
          onError={() => {
            setShowPlayerControls(false);
          }}
          playsInline
          poster={video.fallbackImage.url}
          preload="auto"
          ref={videoRef}
          sources={video.sources}
          onCanPlay={onCanPlay}
        />
        {showPlayerControls && (
          <Slide mounted={inView} direction="left" duration={500}>
            <CustomControls
              disableMuteUnmuteControl={disableMuteUnmuteControl}
              disablePlayPauseControl={disablePlayPauseControl}
              handleMuteToggleClick={handleMuteToggleClick}
              handlePlayToggleClick={handlePlayToggleClick}
              muted={muted}
              playing={playing}
              videoHasAudio={videoHasAudio}
              customControlsPosition={customControlsPosition}
              customControlsAlignment={customControlsAlignment}
            />
          </Slide>
        )}
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const Container = styled.div`
  position: relative;
  height: 100%;
  overflow: hidden;
  width: 100%;
  line-height: 0;
`;

const Video = styled(BaseVideo)<{aspectRatio: `${number} / ${number}`}>`
  height: 100%;
  object-fit: cover;
  object-position: 50% 0;
  width: 100%;

  ${({aspectRatio}) =>
    css`
      aspect-ratio: ${aspectRatio};
    `}
`;
